import { useParams } from "react-router-dom";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "@mantine/hooks";
import {
  newOrderDefaultState,
  getNewOrderDefaultState,
  NewOrderLsKeys,
  NewOrderScenario,
  NewOrderType,
  TNewOrderForm,
  TNewOrderType,
} from "@/entities/new-order";
import { useLazyGetAppInfoQuery } from "@/Store/APIs/Apps.api";
import {
  parseKeywordsToViews,
  parseViewsToKeywords,
} from "@/entities/new-order/utils";
import {
  getDataFromStorage,
  setDataToStorage,
} from "@/Utils/localStorageHelpers";

const typeKeys = {
  [NewOrderType.rate]: "promote-rate-keywords",
  [NewOrderType.install]: "promote-install-keywords",
  [NewOrderType.install_bp]: "promote-install-bp-keywords",
  [NewOrderType.install_with_views]: "promote-install-with-views",
  [NewOrderType.review]: "promote-review-keywords",
};

export function usePromoteOrderForm() {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);

  const [promoteDrafts, setPromoteDrafts] = useLocalStorage<
    Record<string, TNewOrderForm>
  >(NewOrderLsKeys.PROMOTE_ORDER_DRAFT, {});

  const methods = useForm<TNewOrderForm>({
    defaultValues: newOrderDefaultState,
  });

  const { watch, getValues, setValue } = methods;

  const { countriesList, type } = watch();

  const prevOrderTypeRef = useRef<TNewOrderType | null>(null);

  const [getAppInfo] = useLazyGetAppInfoQuery();

  useEffect(() => {
    const initializeForm = async () => {
      try {
        if (!id) {
          return;
        }

        let newCampaignState: TNewOrderForm;

        if (id && promoteDrafts[id]) {
          newCampaignState = promoteDrafts[id];
        } else {
          // Создаем свежую копию дефолтного состояния
          newCampaignState = getNewOrderDefaultState();
          const appInfo = await getAppInfo(Number(id)).unwrap();

          if (appInfo) {
            newCampaignState.app = appInfo;
          }
        }

        methods.reset(
          { ...newCampaignState, activeScenario: NewOrderScenario.promote },
          { keepDefaultValues: false },
        );

        // Инициализируем prevOrderTypeRef после загрузки данных
        if (newCampaignState.type) {
          prevOrderTypeRef.current = newCampaignState.type;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [methods.reset]);

  // Эффект для обработки смены типа заказа с конвертацией данных
  useEffect(() => {
    if (!type || !id) {
      if (!type) {
        Object.values(typeKeys).forEach((key) =>
          localStorage.removeItem(`${key}-${id}`),
        );
      }
      return;
    }

    const oldType = prevOrderTypeRef.current;
    const newType = type;

    // Если тип не изменился, не делаем ничего
    if (oldType === newType) {
      return;
    }

    // Сохраняем/трансформируем данные при переходе с предыдущего типа
    if (oldType && countriesList.length > 0) {
      const isTransitionToViews =
        oldType === NewOrderType.install &&
        newType === NewOrderType.install_with_views;
      const isTransitionFromViews =
        oldType === NewOrderType.install_with_views &&
        newType === NewOrderType.install;
      const isViewsToOther =
        oldType === NewOrderType.install_with_views &&
        newType !== NewOrderType.install;

      if (isTransitionToViews) {
        // install → install_with_views: трансформируем и оставляем
        setValue("countriesList", parseKeywordsToViews(countriesList));
      } else if (isTransitionFromViews) {
        // install_with_views → install: трансформируем и оставляем
        setValue("countriesList", parseViewsToKeywords(countriesList));
      } else if (isViewsToOther) {
        // install_with_views → другой тип: конвертируем в install и сохраняем
        const convertedCountries = parseViewsToKeywords(countriesList);
        setDataToStorage(
          `${typeKeys[NewOrderType.install]}-${id}`,
          convertedCountries,
        );
        setValue("countriesList", []);
      } else {
        // Любой другой переход: сохраняем текущие данные и очищаем
        setDataToStorage(`${typeKeys[oldType]}-${id}`, countriesList);
        setValue("countriesList", []);
      }
    }

    // Загружаем данные для нового типа только если есть oldType (не первая загрузка)
    if (oldType) {
      const savedCountries = getDataFromStorage(`${typeKeys[newType]}-${id}`);
      if (savedCountries) {
        setValue("countriesList", savedCountries);
      }
    }

    prevOrderTypeRef.current = newType;
  }, [type]);

  const onSave = useDebouncedCallback((data: TNewOrderForm) => {
    if (id) {
      setPromoteDrafts({
        ...promoteDrafts,
        [id]: data,
      });
    }
  }, 500);

  const cancelSave = () => {
    onSave.cancel();
  };

  useEffect(() => {
    const subscription = watch(() => {
      const formData = getValues();
      onSave(formData);

      // Сохраняем также данные текущего типа в localStorage
      if (id && formData.type && formData.countriesList.length > 0) {
        setDataToStorage(
          `${typeKeys[formData.type]}-${id}`,
          formData.countriesList,
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, id]);

  const clearDraft = () => {
    if (!id) {
      return;
    }

    const updatedDrafts = { ...promoteDrafts };
    delete updatedDrafts[id];
    setPromoteDrafts(updatedDrafts);
  };

  const handleReset = async () => {
    if (!id) {
      return;
    }

    setIsLoading(true);

    // Отменяем отложенное сохранение
    cancelSave();

    // Создаем свежую копию дефолтного состояния
    const newFormState = getNewOrderDefaultState();
    const appInfo = await getAppInfo(Number(id)).unwrap();

    newFormState.app = appInfo ?? null;

    // Очищаем все сохранённые данные для разных типов заказов
    Object.values(typeKeys).forEach((key) =>
      localStorage.removeItem(`${key}-${id}`),
    );

    // Сбрасываем prevOrderTypeRef перед reset, чтобы избежать загрузки данных
    prevOrderTypeRef.current = newFormState.type ?? null;

    clearDraft();

    methods.reset(
      { ...newFormState, activeScenario: NewOrderScenario.promote },
      { keepDefaultValues: false },
    );

    setIsLoading(false);
  };

  return {
    methods,
    handleReset,
    isLoading,
  };
}
